/**
 * The life map as one SVG.
 *
 * Every box, elbow, label and legend row below lives in a single `<svg>` whose
 * colours are already literal, which is what lets the same drawing be panned
 * on screen and saved to a file the user can print or paste somewhere. Pan and
 * zoom are the `viewBox` — no transform layers, no per-node DOM nodes — so a
 * few hundred entities stay smooth and the exported file is the thing on
 * screen rather than a screenshot of it.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  Brain,
  CheckSquare,
  Download,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Lightbulb,
  Lock,
  MapPin,
  Minus,
  Network,
  Plus,
  Repeat,
  Scan,
  Sparkles,
  Target,
  User,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { JourneyRail } from '../hierarchy/JourneyRail'
import {
  hierarchyGraphService,
  type DreamGraphPayload,
} from '../../services/hierarchyGraphService'
import { buildGraphModel, filterPayloadByKind } from './graphModel'
import {
  boxMetrics as M,
  fonts,
  layoutLifeMap,
  type LaidOutEdge,
  type LaidOutNode,
  type MapShape,
  type Scene,
} from './layout'
import { textWidth } from './measure'
import { crossStroke, paletteFor, readPalette, type Palette } from './palette'
import { downloadPng, downloadSvg } from './exportSvg'

const TYPE_ICON: Record<string, LucideIcon> = {
  dream: Sparkles,
  aspiration: Lightbulb,
  goal: Target,
  skill: Brain,
  habit: Repeat,
  task: CheckSquare,
  action_item: Wrench,
  roadblock: AlertTriangle,
  person: User,
  place: MapPin,
}

const TYPE_ROUTES: Record<string, string> = {
  dream: '/dreams',
  goal: '/goals',
  task: '/tasks',
  skill: '/skills',
  habit: '/habits',
  person: '/my-people/person',
  place: '/places',
}

/** Where a block opens. Places have a list, not a page of their own, so a
 *  place opens the list — `/places/<id>` used to land on Not Found. */
function routeFor(type: string, id: number | string): string | null {
  const base = TYPE_ROUTES[type]
  if (!base) return null
  return type === 'place' ? base : `${base}/${id}`
}

const LEGEND_H = 38
const FIT_PAD = 30

/**
 * How the map is drawn, as opposed to how it is arranged.
 *
 *  soft      — filled, rounded, softly shadowed. Warm, and the type of a thing
 *              is legible from its colour before you read a word of it.
 *  blueprint — a gridded sheet of line art. Reads as a document rather than an
 *              app, prints without eating ink, and stays clear at a zoom where
 *              filled boxes turn into coloured mush.
 */
export type MapSkin = 'soft' | 'blueprint'

const SHAPES: Array<{ key: MapShape; label: string }> = [
  { key: 'branches', label: 'Branches' },
  { key: 'roadmap', label: 'Roadmap' },
  { key: 'constellation', label: 'Constellation' },
]

const SKINS: Array<{ key: MapSkin; label: string }> = [
  { key: 'soft', label: 'Soft' },
  { key: 'blueprint', label: 'Blueprint' },
]

const SHAPE_KEY = 'horton-lifemap-shape'
const SKIN_KEY = 'horton-lifemap-skin'

function stored<T extends string>(key: string, allowed: T[], fallback: T): T {
  if (typeof localStorage === 'undefined') return fallback
  const got = localStorage.getItem(key) as T | null
  return got && allowed.includes(got) ? got : fallback
}

interface ViewBox {
  x: number
  y: number
  w: number
  h: number
}

export interface LifeMapDiagramProps {
  /** Required in `branch` mode. */
  nodeId?: number
  /** Required in `branch` mode. */
  nodeType?: string
  mode?: 'branch' | 'full'
  /** Stretch to fill a flex parent (the /life-map page) instead of a fixed height. */
  fillHeight?: boolean
  compact?: boolean
  /** 'dream' | 'goal' | 'habit' | 'skill' | 'task' | 'world' | null */
  kindFilter?: string | null
  showFullViewButton?: boolean
}

// ── theme ───────────────────────────────────────────────────────────────────

/**
 * The palette has to be read from the live document, so it is state rather
 * than a constant, and it has to be re-read when the theme changes. Watching
 * `<html>` covers every route into a theme switch (the picker, the diary
 * toggle, a system dark-mode change) without this file knowing about any of
 * them.
 */
function useLivePalette(): Palette {
  const [palette, setPalette] = useState<Palette>(() => readPalette())

  useEffect(() => {
    const root = document.documentElement
    let frame = 0
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame)
      // Coalesce: a theme switch touches several attributes at once, and each
      // palette read is a forced style resolve.
      frame = requestAnimationFrame(() => setPalette(readPalette()))
    })
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['class', 'style', 'data-theme', 'data-accent'],
    })
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [])

  return palette
}

// ── node ────────────────────────────────────────────────────────────────────

interface NodeProps {
  n: LaidOutNode
  palette: Palette
  skin: MapSkin
  shadow: boolean
  dimmed: boolean
  interactive: boolean
  onEnter: (key: string | null) => void
  onClick: (n: LaidOutNode) => void
}

function DiagramNode({
  n,
  palette,
  skin,
  shadow,
  dimmed,
  interactive,
  onEnter,
  onClick,
}: NodeProps) {
  const p = paletteFor(palette, n.node.type)
  const Icon = TYPE_ICON[n.node.type] ?? Network
  const blockers = n.node.active_blocker_count ?? 0
  const kindY = M.padTop + M.kindLineH - 3
  const titleTop = M.padTop + M.kindLineH + M.titleLineH - 4.5
  const line = skin === 'blueprint'
  const radius = line ? 4 : M.radius

  return (
    <g
      transform={`translate(${n.x} ${n.y})`}
      opacity={dimmed ? 0.26 : n.isGhost ? 0.62 : 1}
      style={interactive ? { cursor: 'pointer' } : undefined}
      onMouseEnter={() => onEnter(n.key)}
      onMouseLeave={() => onEnter(null)}
      onClick={() => onClick(n)}
      data-lifemap-node={n.key}
      data-node-type={n.node.type}
    >
      <title>
        {n.isGhost
          ? `${n.node.title} — a step you have not started yet`
          : `${n.kindLabel.toLowerCase()}: ${n.node.title}`}
      </title>

      {n.node.is_current && (
        <rect
          x={-3.5}
          y={-3.5}
          width={n.w + 7}
          height={n.h + 7}
          rx={radius + 3.5}
          fill="none"
          stroke={palette.brand}
          strokeWidth={2}
          opacity={0.55}
        />
      )}

      <rect
        width={n.w}
        height={n.h}
        rx={radius}
        fill={
          line
            ? palette.surface
            : `url(#lm-fill-${n.node.type in palette.types ? n.node.type : 'other'})`
        }
        stroke={line ? palette.textMuted : p.stroke}
        strokeWidth={line ? 1 : 1.25}
        strokeOpacity={line ? 0.55 : 1}
        strokeDasharray={n.isGhost ? '5 3' : undefined}
        filter={!line && shadow ? 'url(#lm-shadow)' : undefined}
      />

      {/* A colour chip on the leading edge: the type is legible at a zoom
          where the KIND caption is not. Blueprint keeps the chip but draws it
          as a hairline rule, so the sheet stays line art and the type is still
          there to be read. */}
      {line ? (
        <path
          d={`M 1.5 ${radius} L 1.5 ${n.h - radius}`}
          stroke={p.stroke}
          strokeWidth={2.5}
          opacity={n.isGhost ? 0.35 : 0.8}
        />
      ) : (
        <path
          d={`M 0 ${radius} A ${radius} ${radius} 0 0 1 ${radius} 0 L ${radius} ${n.h} A ${radius} ${radius} 0 0 1 0 ${n.h - radius} Z`}
          fill={p.stroke}
          opacity={n.isGhost ? 0.3 : 0.75}
        />
      )}

      <Icon
        x={M.padX + 1}
        y={M.padTop + 2}
        size={15}
        color={p.stroke}
        strokeWidth={2}
      />

      <text
        x={M.textX}
        y={kindY}
        fontFamily={palette.fontFamily}
        fontSize={M.kindSize}
        fontWeight={600}
        letterSpacing={0.7}
        fill={palette.textMuted}
      >
        {n.kindLabel}
      </text>

      {n.titleLines.map((line, i) => (
        <text
          key={i}
          x={M.textX}
          y={titleTop + i * M.titleLineH}
          fontFamily={palette.fontFamily}
          fontSize={M.titleSize}
          fontWeight={600}
          fill={palette.text}
        >
          {line}
        </text>
      ))}

      {n.subtitle && (
        <text
          x={M.textX}
          y={M.padTop + M.kindLineH + n.titleLines.length * M.titleLineH + M.subLineH - 4}
          fontFamily={palette.fontFamily}
          fontSize={M.subSize}
          fontStyle="italic"
          fill={palette.textMuted}
          opacity={0.85}
        >
          {n.subtitle}
        </text>
      )}

      {n.isGhost && (
        <Lock
          x={n.w - 17}
          y={5}
          size={11}
          color={palette.textMuted}
          strokeWidth={2.2}
        />
      )}

      {!n.isGhost && blockers > 0 && (
        <g transform={`translate(${n.w - 9} 9)`}>
          <circle r={9} fill={palette.warning} />
          <text
            textAnchor="middle"
            y={3.4}
            fontFamily={palette.fontFamily}
            fontSize={10}
            fontWeight={700}
            fill="#ffffff"
          >
            {blockers}
          </text>
          <title>
            {blockers === 1
              ? 'one thing is getting in the way'
              : `${blockers} things are getting in the way`}
          </title>
        </g>
      )}
    </g>
  )
}

// ── legend ──────────────────────────────────────────────────────────────────

interface LegendItem {
  label: string
  stroke: string
  dash?: string
}

function buildLegend(
  scene: Scene,
  palette: Palette,
  structuralStroke: string,
): LegendItem[] {
  const items: LegendItem[] = []
  if (scene.structural.length > 0) {
    items.push({ label: 'is part of', stroke: structuralStroke })
  }
  const seen = new Set<string>()
  for (const e of scene.cross) {
    const label = e.label ?? e.relationship
    if (seen.has(label)) continue
    seen.add(label)
    const s = crossStroke(palette, e.relationship)
    items.push({ label, stroke: s.stroke, dash: s.dash })
  }
  return items
}

// ── the view ────────────────────────────────────────────────────────────────

function CrossEdge({
  e,
  palette,
  dimmed,
  markerId,
}: {
  e: LaidOutEdge
  palette: Palette
  dimmed: boolean
  markerId: string
}) {
  const s = crossStroke(palette, e.relationship)
  return (
    <g opacity={dimmed ? 0.14 : 0.85}>
      <path
        d={e.path}
        fill="none"
        stroke={s.stroke}
        strokeWidth={s.width}
        strokeDasharray={s.dash}
        strokeLinecap="round"
        markerEnd={`url(#${markerId})`}
      />
      {e.label && (
        <>
          <rect
            x={e.labelX - textWidth(e.label, fonts(palette.fontFamily).edgeLabel) / 2 - 4}
            y={e.labelY - 8}
            width={textWidth(e.label, fonts(palette.fontFamily).edgeLabel) + 8}
            height={14}
            rx={4}
            fill={palette.surface}
            opacity={0.92}
          />
          <text
            x={e.labelX}
            y={e.labelY + 2.5}
            textAnchor="middle"
            fontFamily={palette.fontFamily}
            fontSize={10}
            fontWeight={500}
            fill={s.stroke}
          >
            {e.label}
          </text>
        </>
      )}
    </g>
  )
}

export default function LifeMapDiagram({
  nodeId,
  nodeType,
  mode = 'branch',
  fillHeight = false,
  compact = false,
  kindFilter = null,
  showFullViewButton = false,
}: LifeMapDiagramProps) {
  const navigate = useNavigate()
  const palette = useLivePalette()

  const [payload, setPayload] = useState<DreamGraphPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCross, setShowCross] = useState(true)
  const [hoverKey, setHoverKey] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  // Which look the user last chose. Remembered because it is a taste, not a
  // per-visit decision, and being asked again every time is the annoying part.
  const [shape, setShapeState] = useState<MapShape>(() =>
    stored(SHAPE_KEY, SHAPES.map((s) => s.key), 'branches'),
  )
  const [skin, setSkinState] = useState<MapSkin>(() =>
    stored(SKIN_KEY, SKINS.map((s) => s.key), 'soft'),
  )
  const setShape = (next: MapShape) => {
    setShapeState(next)
    localStorage.setItem(SHAPE_KEY, next)
  }
  const setSkin = (next: MapSkin) => {
    setSkinState(next)
    localStorage.setItem(SKIN_KEY, next)
  }

  const line = skin === 'blueprint'
  const structuralStroke = line ? palette.textMuted : palette.brand

  const svgRef = useRef<SVGSVGElement | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const [vb, setVb] = useState<ViewBox | null>(null)
  /**
   * Quantised on purpose: the packing target is derived from this, so an
   * unrounded value would relayout the whole map on every pixel of a window
   * drag. One decimal is finer than the eye can tell in a packed poster.
   */
  const [paneAspect, setPaneAspect] = useState(1.7)
  /** Once the user has panned or zoomed, a resize must not yank the view back. */
  const touched = useRef(false)
  const fitVb = useRef<ViewBox | null>(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(null)

    const fetcher =
      mode === 'full'
        ? hierarchyGraphService.getFullLifeMap()
        : nodeId !== undefined && nodeType
          ? hierarchyGraphService.getDreamGraph(nodeType, nodeId)
          : Promise.resolve<DreamGraphPayload>({
              root: null,
              current: { id: nodeId ?? 0, type: nodeType ?? '' },
              nodes: [],
              edges: [],
            })

    fetcher
      .then((data) => {
        if (active) setPayload(data)
      })
      .catch((err: Error) => {
        if (active) setError(err.message || 'Could not load graph')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [nodeId, nodeType, mode])

  const model = useMemo(
    () => (payload ? buildGraphModel(filterPayloadByKind(payload, kindFilter)) : null),
    [payload, kindFilter],
  )

  const scene = useMemo(
    () =>
      model
        ? layoutLifeMap(model, {
            fontFamily: palette.fontFamily,
            frames: mode === 'full',
            aspect: paneAspect,
            shape,
          })
        : null,
    [model, palette.fontFamily, mode, paneAspect, shape],
  )

  const legend = useMemo(
    () => (scene ? buildLegend(scene, palette, structuralStroke) : []),
    [scene, palette, structuralStroke],
  )

  /**
   * Scene bounds including the legend strip — what "fit" and export frame.
   * Blueprint adds a margin, because the sheet it draws needs to be a sheet
   * with an edge and not a grid that runs off into nothing.
   */
  const bounds = useMemo(() => {
    if (!scene || scene.width === 0) return null
    const base = {
      x: 0,
      y: 0,
      width: scene.width,
      height: scene.height + (legend.length > 0 ? LEGEND_H : 0),
    }
    if (!line) return base
    const pad = 30
    return {
      x: base.x - pad,
      y: base.y - pad,
      width: base.width + pad * 2,
      height: base.height + pad * 2,
    }
  }, [scene, legend.length, line])

  /** One arrow marker per colour actually in use — markers cannot inherit. */
  const markers = useMemo(() => {
    const colors = new Set<string>([palette.brand, palette.textMuted])
    for (const e of scene?.cross ?? []) {
      colors.add(crossStroke(palette, e.relationship).stroke)
    }
    return [...colors].map((color, i) => ({ id: `lm-arrow-${i}`, color }))
  }, [scene, palette])

  const markerFor = useCallback(
    (color: string) => markers.find((m) => m.color === color)?.id ?? markers[0]?.id ?? '',
    [markers],
  )

  const computeFit = useCallback((): ViewBox | null => {
    const el = wrapRef.current
    if (!el || !bounds) return null
    let w = bounds.width + FIT_PAD * 2
    let h = bounds.height + FIT_PAD * 2
    const cw = el.clientWidth || 1
    const ch = el.clientHeight || 1
    // Grow the shorter axis to the container's aspect so "fit" fills the pane
    // instead of letterboxing inside it.
    if (cw / ch > w / h) w = h * (cw / ch)
    else h = w / (cw / ch)
    return {
      x: bounds.x - (w - bounds.width) / 2,
      y: bounds.y - (h - bounds.height) / 2,
      w,
      h,
    }
  }, [bounds])

  const fit = useCallback(() => {
    const next = computeFit()
    if (!next) return
    fitVb.current = next
    touched.current = false
    setVb(next)
  }, [computeFit])

  // Refit whenever the drawing itself changes (new data, new filter, theme).
  useLayoutEffect(() => {
    const next = computeFit()
    fitVb.current = next
    touched.current = false
    setVb(next)
  }, [computeFit])

  useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth
      const h = el.clientHeight
      if (w > 0 && h > 0) {
        setPaneAspect(Math.round(Math.min(4, Math.max(0.5, w / h)) * 10) / 10)
      }
      if (touched.current) return
      const next = computeFit()
      if (!next) return
      fitVb.current = next
      setVb(next)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [computeFit])

  const zoomBy = useCallback((factor: number, about?: { x: number; y: number }) => {
    setVb((prev) => {
      if (!prev) return prev
      const base = fitVb.current
      const minW = base ? base.w / 14 : 40
      const maxW = base ? base.w * 4 : 40000
      const w = Math.min(maxW, Math.max(minW, prev.w * factor))
      const k = w / prev.w
      const cx = about?.x ?? prev.x + prev.w / 2
      const cy = about?.y ?? prev.y + prev.h / 2
      touched.current = true
      return {
        x: cx - (cx - prev.x) * k,
        y: cy - (cy - prev.y) * k,
        w,
        h: prev.h * k,
      }
    })
  }, [])

  // Wheel zoom about the cursor. Registered by hand because React's onWheel is
  // passive and cannot preventDefault, which would let the page scroll away
  // underneath the map.
  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      const ctm = svg.getScreenCTM()
      let about: { x: number; y: number } | undefined
      if (ctm) {
        const pt = svg.createSVGPoint()
        pt.x = event.clientX
        pt.y = event.clientY
        const u = pt.matrixTransform(ctm.inverse())
        about = { x: u.x, y: u.y }
      }
      zoomBy(Math.exp(event.deltaY * 0.0016), about)
    }
    svg.addEventListener('wheel', onWheel, { passive: false })
    return () => svg.removeEventListener('wheel', onWheel)
  }, [zoomBy, scene])

  const drag = useRef<{ x: number; y: number; moved: number } | null>(null)

  const onPointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    if (event.button !== 0) return
    drag.current = { x: event.clientX, y: event.clientY, moved: 0 }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const d = drag.current
    const svg = svgRef.current
    if (!d || !svg || !vb) return
    const dxClient = event.clientX - d.x
    const dyClient = event.clientY - d.y
    d.moved += Math.abs(dxClient) + Math.abs(dyClient)
    d.x = event.clientX
    d.y = event.clientY
    const scale = vb.w / (svg.clientWidth || 1)
    touched.current = true
    setVb({ ...vb, x: vb.x - dxClient * scale, y: vb.y - dyClient * scale })
  }

  const onPointerUp = (event: React.PointerEvent<SVGSVGElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    // Leave the flag for the click handler on this same gesture to read.
    setTimeout(() => {
      drag.current = null
    }, 0)
  }

  const onKeyDown = (event: React.KeyboardEvent<SVGSVGElement>) => {
    if (!vb) return
    const step = vb.w * 0.08
    const map: Record<string, () => void> = {
      ArrowLeft: () => setVb({ ...vb, x: vb.x - step }),
      ArrowRight: () => setVb({ ...vb, x: vb.x + step }),
      ArrowUp: () => setVb({ ...vb, y: vb.y - step }),
      ArrowDown: () => setVb({ ...vb, y: vb.y + step }),
      '+': () => zoomBy(1 / 1.25),
      '=': () => zoomBy(1 / 1.25),
      '-': () => zoomBy(1.25),
      '0': fit,
    }
    const action = map[event.key]
    if (!action) return
    event.preventDefault()
    touched.current = true
    action()
  }

  const openNode = useCallback(
    (n: LaidOutNode) => {
      if (drag.current && drag.current.moved > 5) return
      if (n.isGhost) return
      if (n.node.id === nodeId && n.node.type === nodeType) return
      const route = routeFor(n.node.type, n.node.id)
      if (route) navigate(route)
    },
    [navigate, nodeId, nodeType],
  )

  /** Hovering a node fades everything it does not touch. */
  const focus = useMemo(() => {
    if (!hoverKey || !scene) return null
    const set = new Set<string>([hoverKey])
    for (const e of [...scene.structural, ...scene.cross]) {
      if (e.sourceKey === hoverKey) set.add(e.targetKey)
      if (e.targetKey === hoverKey) set.add(e.sourceKey)
    }
    return set
  }, [hoverKey, scene])

  const save = async (kind: 'svg' | 'png') => {
    const svg = svgRef.current
    if (!svg || !bounds) return
    const opts = {
      viewBox: bounds,
      background: palette.surface,
      filename: `life-map-${new Date().toISOString().slice(0, 10)}`,
    }
    if (kind === 'svg') {
      downloadSvg(svg, opts)
      return
    }
    setSaving(true)
    try {
      await downloadPng(svg, opts)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'the image could not be saved')
    } finally {
      setSaving(false)
    }
  }

  const journeyPanels = useMemo(() => {
    if (!payload?.journeys?.length) return []
    const titles = new Map<number, string>()
    for (const n of payload.nodes) {
      if (n.type === 'goal') titles.set(n.id, n.title)
    }
    return payload.journeys
      .filter((j) => j.steps.length > 0)
      .map((j) => ({ journey: j, goalTitle: titles.get(j.goal_id) ?? `Goal #${j.goal_id}` }))
  }, [payload])

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 text-sm text-secondary">
          <Network className="w-4 h-4 animate-pulse" />
          Loading life-map graph…
        </div>
      </Card>
    )
  }
  if (error && !scene) {
    return (
      <Card className="p-6">
        <div className="text-sm text-secondary">Could not load graph: {error}</div>
      </Card>
    )
  }
  if (!payload || !scene || scene.nodes.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-sm text-secondary">
          {mode === 'full'
            ? 'Your life map is empty. Start by creating a dream.'
            : 'No hierarchy graph yet for this item.'}
        </div>
      </Card>
    )
  }

  const shadow = scene.nodes.length <= 220
  const f = fonts(palette.fontFamily)
  const legendY = scene.height + 22
  const canvasHeight = compact ? 'clamp(280px, 42vh, 400px)' : 'clamp(340px, 58vh, 620px)'

  let legendX = 2
  const legendPlaced = legend.map((item) => {
    const at = legendX
    legendX += 26 + 6 + textWidth(item.label, f.edgeLabel) + 22
    return { ...item, x: at }
  })

  return (
    <Card
      className={`p-0 overflow-hidden ${fillHeight ? 'flex-1 min-h-0 flex flex-col' : ''}`}
    >
      {journeyPanels.length > 0 && (
        <div className="px-4 py-3 border-b border-border/50 flex flex-col gap-3">
          {journeyPanels.map(({ journey, goalTitle }) => (
            <JourneyRail
              key={`rail-${journey.plan_id ?? journey.goal_id}`}
              journey={journey}
              goalTitle={goalTitle}
              onNavigate={(entityType, entityId) => {
                const route = routeFor(entityType, entityId)
                if (route) navigate(route)
              }}
              showGoalTitle={mode === 'full' || journeyPanels.length > 1}
            />
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-border/50 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Network className="w-4 h-4" style={{ color: 'var(--brand-primary)' }} />
            {mode === 'full' ? 'Full life map' : 'Life-map graph'}
          </div>
          {/* A compact embed is a glance, not a place to sit and restyle. */}
          <div
            className={`flex items-center gap-1 ${compact ? 'hidden' : ''}`}
            role="group"
            aria-label="Map shape"
          >
            {SHAPES.map((s) => (
              <button
                key={s.key}
                type="button"
                aria-pressed={shape === s.key}
                onClick={() => setShape(s.key)}
                className={`px-2.5 py-1 rounded-full text-app-nano transition-colors ${
                  shape === s.key
                    ? 'bg-brand text-on-brand font-semibold'
                    : 'text-secondary hover:text-brand'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div
            className={`flex items-center gap-1 pl-2 border-l border-soft ${compact ? 'hidden' : ''}`}
            role="group"
            aria-label="Map style"
          >
            {SKINS.map((s) => (
              <button
                key={s.key}
                type="button"
                aria-pressed={skin === s.key}
                onClick={() => setSkin(s.key)}
                className={`px-2.5 py-1 rounded-full text-app-nano transition-colors ${
                  skin === s.key
                    ? 'bg-brand text-on-brand font-semibold'
                    : 'text-secondary hover:text-brand'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="text-xs text-secondary mr-1.5">
            {scene.nodes.length} nodes · {scene.structural.length + scene.cross.length} edges
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowCross((v) => !v)}
            title={showCross ? 'Hide cross-links' : 'Show cross-links'}
          >
            {showCross ? (
              <EyeOff className="w-3.5 h-3.5 mr-1.5" />
            ) : (
              <Eye className="w-3.5 h-3.5 mr-1.5" />
            )}
            {showCross ? 'Hide links' : 'Show links'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => zoomBy(1.25)} title="Zoom out">
            <Minus className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => zoomBy(1 / 1.25)} title="Zoom in">
            <Plus className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="ghost" onClick={fit} title="Fit the whole map">
            <Scan className="w-3.5 h-3.5 mr-1.5" />
            Fit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => save('svg')}
            title="Save the whole map as an SVG you can print or share"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            SVG
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={saving}
            onClick={() => save('png')}
            title="Save the whole map as an image"
          >
            <ImageIcon className="w-3.5 h-3.5 mr-1.5" />
            {saving ? 'Saving…' : 'PNG'}
          </Button>
          {showFullViewButton && (
            // A link, not a button (owner, 2026-09-12): the whole map is a
            // page you go to, and the item page should say so plainly.
            <Link
              to="/life-map"
              className="inline-flex min-h-touch items-center px-2 text-app-meta font-medium text-brand hover:underline"
            >
              Life Map →
            </Link>
          )}
        </div>
      </div>

      {error && (
        <div className="px-4 py-2 text-xs text-secondary border-b border-border/50">
          {error}
        </div>
      )}

      <div
        ref={wrapRef}
        className={`w-full ${fillHeight ? 'flex-1 min-h-0' : ''}`}
        style={fillHeight ? undefined : { height: canvasHeight }}
      >
        <svg
          ref={svgRef}
          role="img"
          aria-label={
            mode === 'full'
              ? 'Your full life map, drawn as a flow diagram'
              : 'This item and what it connects to, drawn as a flow diagram'
          }
          tabIndex={0}
          viewBox={vb ? `${vb.x} ${vb.y} ${vb.w} ${vb.h}` : undefined}
          width="100%"
          height="100%"
          style={{
            display: 'block',
            touchAction: 'none',
            cursor: drag.current ? 'grabbing' : 'grab',
            outline: 'none',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onKeyDown={onKeyDown}
        >
          <desc>
            {`${scene.nodes.length} things on your life map, with ${scene.structural.length} parent links and ${scene.cross.length} cross-links.`}
          </desc>

          <defs>
            {Object.entries(palette.types).map(([type, p]) => (
              <linearGradient
                key={type}
                id={`lm-fill-${type}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={p.fillTop} />
                <stop offset="100%" stopColor={p.fill} />
              </linearGradient>
            ))}
            <linearGradient id="lm-fill-other" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={palette.fallbackType.fillTop} />
              <stop offset="100%" stopColor={palette.fallbackType.fill} />
            </linearGradient>

            {markers.map((m) => (
              <marker
                key={m.id}
                id={m.id}
                viewBox="0 0 9 9"
                refX={8}
                refY={4.5}
                markerWidth={9}
                markerHeight={9}
                markerUnits="userSpaceOnUse"
                orient="auto"
              >
                <path d="M 0 0.9 L 8.4 4.5 L 0 8.1 Z" fill={m.color} />
              </marker>
            ))}

            <filter id="lm-shadow" x="-25%" y="-25%" width="150%" height="170%">
              <feDropShadow
                dx="0"
                dy="1.5"
                stdDeviation="2.4"
                floodColor={palette.text}
                floodOpacity="0.11"
              />
            </filter>

            {/* Graph paper: a fine ruling with a heavier line every fifth
                cell. The coarse line is what makes it read as paper rather
                than as a screen door. */}
            <pattern
              id="lm-grid-fine"
              width={24}
              height={24}
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 24 0 L 0 0 L 0 24"
                fill="none"
                stroke={palette.textMuted}
                strokeWidth={0.6}
                opacity={0.16}
              />
            </pattern>
            <pattern
              id="lm-grid"
              width={120}
              height={120}
              patternUnits="userSpaceOnUse"
            >
              <rect width={120} height={120} fill="url(#lm-grid-fine)" />
              <path
                d="M 120 0 L 0 0 L 0 120"
                fill="none"
                stroke={palette.textMuted}
                strokeWidth={0.8}
                opacity={0.3}
              />
            </pattern>
          </defs>

          {/* Blueprint draws an actual sheet — paper, ruling, an edge — so the
              map reads as a document you could pin up rather than a viewport
              onto something larger. */}
          {line && bounds && (
            <g>
              <rect
                x={bounds.x}
                y={bounds.y}
                width={bounds.width}
                height={bounds.height}
                fill={palette.surface}
              />
              <rect
                x={bounds.x}
                y={bounds.y}
                width={bounds.width}
                height={bounds.height}
                fill="url(#lm-grid)"
              />
              <rect
                x={bounds.x + 0.5}
                y={bounds.y + 0.5}
                width={bounds.width - 1}
                height={bounds.height - 1}
                fill="none"
                stroke={palette.textMuted}
                strokeWidth={1}
                strokeOpacity={0.45}
              />
            </g>
          )}

          {/* One soft frame per dream tree, so a map of six dreams reads as six
              things rather than one thicket. */}
          <g>
            {scene.frames.map((frame) => (
              <g key={frame.rootKey}>
                <rect
                  x={frame.x}
                  y={frame.y}
                  width={frame.w}
                  height={frame.h}
                  rx={line ? 6 : 18}
                  fill={line ? 'none' : palette.surfaceAlt}
                  fillOpacity={line ? undefined : 0.42}
                  stroke={line ? palette.textMuted : palette.borderSoft}
                  strokeWidth={1}
                  strokeOpacity={line ? 0.35 : 1}
                  strokeDasharray={line ? '4 4' : undefined}
                />
                <text
                  x={frame.x + 18}
                  y={frame.y + 18}
                  fontFamily={palette.fontFamily}
                  fontSize={12}
                  fontWeight={600}
                  letterSpacing={0.3}
                  fill={palette.textMuted}
                >
                  {frame.title}
                </text>
              </g>
            ))}

            {/* One faint circle per generation, so the rings read as
                deliberate distance from the dream rather than scatter. */}
            {scene.rings.map((ring, i) => (
              <circle
                key={`ring-${i}`}
                data-lifemap-ring={i}
                cx={ring.cx}
                cy={ring.cy}
                r={ring.r}
                fill="none"
                stroke={palette.textMuted}
                strokeWidth={1}
                strokeDasharray="2 6"
                opacity={0.3}
              />
            ))}
          </g>

          {showCross && (
            <g>
              {scene.cross.map((e) => (
                <CrossEdge
                  key={e.id}
                  e={e}
                  palette={palette}
                  dimmed={
                    !!focus && !(focus.has(e.sourceKey) && focus.has(e.targetKey))
                  }
                  markerId={markerFor(crossStroke(palette, e.relationship).stroke)}
                />
              ))}
            </g>
          )}

          <g>
            {scene.structural.map((e) => (
              <path
                key={e.id}
                d={e.path}
                fill="none"
                stroke={structuralStroke}
                strokeWidth={line ? 1.1 : 1.5}
                strokeOpacity={
                  !focus || (focus.has(e.sourceKey) && focus.has(e.targetKey))
                    ? line
                      ? 0.62
                      : 0.5
                    : 0.12
                }
                strokeDasharray={e.relationship === 'planned' ? '5 4' : undefined}
                strokeLinecap="round"
                markerEnd={`url(#${markerFor(structuralStroke)})`}
              />
            ))}
          </g>

          <g>
            {scene.nodes.map((n) => (
              <DiagramNode
                key={n.key}
                n={n}
                palette={palette}
                skin={skin}
                shadow={shadow}
                dimmed={!!focus && !focus.has(n.key)}
                interactive={!n.isGhost && !!TYPE_ROUTES[n.node.type]}
                onEnter={setHoverKey}
                onClick={openNode}
              />
            ))}
          </g>

          {legendPlaced.length > 0 && (
            <g>
              {legendPlaced.map((item) => (
                <g key={item.label} transform={`translate(${item.x} ${legendY})`}>
                  <path
                    d="M 0 0 L 24 0"
                    stroke={item.stroke}
                    strokeWidth={1.6}
                    strokeDasharray={item.dash}
                    strokeLinecap="round"
                  />
                  <text
                    x={30}
                    y={3.6}
                    fontFamily={palette.fontFamily}
                    fontSize={10}
                    fontWeight={500}
                    fill={palette.textMuted}
                  >
                    {item.label}
                  </text>
                </g>
              ))}
            </g>
          )}
        </svg>
      </div>

      <div className="px-4 py-1.5 text-app-nano text-secondary border-t border-border/50">
        Scroll to zoom · drag to move · click anything to open it
      </div>
    </Card>
  )
}
